import React, { useEffect, useRef, useState } from 'react';
import LocationMap from '../../LocationMap';
import { getProject, saveIdentification, updateProject } from '../data/projects';
import { identifyProperty, manualSource, propertySignature, fetchBuildingFile, buildingFileLogEvent } from '../data/identification';
import { uploadFile } from '../data/upload';
import Roadmap, { reachedStageFor } from '../components/Roadmap';
import AgentActivity from '../components/AgentActivity';
import DocumentArchive from '../components/DocumentArchive';
import './PropertyIdentification.css';

// Stage 02 — "זיהוי הנכס" (property identification). The first working screen
// after a project is opened+saved: it identifies the property and aggregates the
// sources found, building an ordered base for the next stage. It does NOT analyze
// plans, extract areas, or compute rights (spec chunk 02, §"what is NOT here").

const ACTIVE_STAGE = 1; // "זיהוי ואיסוף נתונים" — this screen's stage in the roadmap

const SRC_STATUS = {
  collected: { label: 'נאסף', tone: 'ok' },
  empty: { label: 'לא נמצא', tone: 'neutral' },
  pending: { label: 'טרם נאסף', tone: 'neutral' },
  planned: { label: 'ייאסף בניתוח', tone: 'info' },
  scanning: { label: 'סורק…', tone: 'warn' },
  failed: { label: 'נכשל', tone: 'bad' },
  partial: { label: 'חלקי', tone: 'warn' },
};

const ID_STATUS = {
  identified: { label: 'זוהה', tone: 'ok' },
  partial: { label: 'זוהה חלקית', tone: 'warn' },
  unidentified: { label: 'לא זוהה', tone: 'bad' },
};
const CONFIDENCE = { high: 'גבוהה', medium: 'בינונית', low: 'נמוכה' };

function nowTime() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;
}

// DD.MM.YY for the sources collection-date column (spec §6.1).
function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getFullYear()).slice(2)}`;
}

function Pill({ tone, children }) {
  return <span className={`pid-pill pid-pill-${tone}`}>{children}</span>;
}

// ── Box 1: property identification ───────────────────────────────────────────
function IdentificationBox({ id, parcel, loading }) {
  const rows = [
    ['כתובת', id?.address],
    ['כתובת נוספת', id?.additionalAddress],
    ['גוש', id?.gush],
    ['חלקה', id?.helka],
    ['תת-חלקה', id?.subParcel],
    ['קואורדינטות (ITM)', id?.coordinates ? `${id.coordinates.x}, ${id.coordinates.y}` : null],
    ['מקור זיהוי', id?.mapSource],
  ];
  const st = ID_STATUS[id?.status] || ID_STATUS.unidentified;
  return (
    <section className="pid-card pid-idbox">
      <header className="pid-card-head">
        <i className="fi fi-rr-marker" />
        <h3>זיהוי הנכס</h3>
        {!loading && id && (
          <div className="pid-idbox-status">
            <Pill tone={st.tone}>{st.label}</Pill>
            {id.confidence && <span className="pid-conf">רמת ודאות: {CONFIDENCE[id.confidence]}</span>}
          </div>
        )}
      </header>
      <div className="pid-idbox-body">
        <dl className="pid-facts">
          {rows.map(([label, value]) => (
            <div className="pid-fact" key={label}>
              <dt>{label}</dt>
              <dd className={`${value ? '' : 'is-empty'}${label.includes('קואורדינ') ? ' pid-coord' : ''}`}>
                {loading ? <span className="rp-skel" style={{ display: 'inline-block', width: 90, height: 12 }} /> : (value || '—')}
              </dd>
            </div>
          ))}
        </dl>
        <div className="pid-idmap">
          {parcel?.coords ? (
            <LocationMap
              coordinates={parcel.coords}
              geometry={parcel.geometry}
              showStreetView={false}
              showTitles={false}
              stacked
              flat
              useStatic
              zoomDelta={parcel.geometry ? -1 : 1}
            />
          ) : (
            <div className="pid-idmap-empty">
              <i className="fi fi-rr-map-marker" />
              <span>{loading ? 'מאתר את הנכס…' : 'לא הוצג מיקום'}</span>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

// ── Box 2: sources collected ─────────────────────────────────────────────────
function SourcesBox({ sources, loading, onRemove }) {
  return (
    <section className="pid-card pid-sources">
      <header className="pid-card-head">
        <i className="fi fi-rr-layers" />
        <h3>מקורות שנאספו</h3>
        {!loading && <span className="pid-count">{sources.length}</span>}
      </header>
      <div className="pid-table">
        <div className="pid-tr pid-tr-head">
          <span className="pid-th pid-c-num">מס׳</span>
          <span className="pid-th">מקור</span>
          <span className="pid-th pid-c-type">סוג</span>
          <span className="pid-th pid-c-status">סטטוס</span>
          <span className="pid-th pid-c-date">תאריך איסוף</span>
          <span className="pid-th pid-c-act" />
        </div>
        {loading && <div className="pid-table-loading"><span className="rp-spinner" /> אוסף מקורות…</div>}
        {!loading && sources.map((s) => {
          const st = SRC_STATUS[s.status] || SRC_STATUS.empty;
          return (
            <div className="pid-tr" key={s.id}>
              <span className="pid-td pid-c-num">{s.number}</span>
              <span className="pid-td">
                <span className="pid-src-name">
                  {s.source}
                  {s.manual && <span className="pid-src-badge">ידני</span>}
                </span>
                {s.note && <span className="pid-src-note">{s.note}</span>}
                {(s.datasets || []).length > 0 && (
                  <span className="pid-src-datasets">
                    {s.datasets.map((d) => (d.link
                      ? (
                        <a key={d.id} className={`pid-ds-chip pid-ds-link ${d.status === 'failed' || d.status === 'empty' ? 'is-off' : ''}`} href={d.link} target="_blank" rel="noreferrer" title={`שכבת מידע: ${d.name} — פתיחת המקור`}>
                          {d.name} <i className="fi fi-rr-arrow-up-right-from-square" />
                        </a>
                      )
                      : (
                        <span key={d.id} className={`pid-ds-chip ${d.status === 'failed' || d.status === 'empty' ? 'is-off' : ''}`} title={`שכבת מידע: ${d.name}`}>
                          {d.name}
                        </span>
                      )))}
                  </span>
                )}
              </span>
              <span className="pid-td pid-c-type">{s.type}</span>
              <span className="pid-td pid-c-status"><Pill tone={st.tone}>{st.label}</Pill></span>
              <span className="pid-td pid-c-date">{fmtDate(s.date)}</span>
              <span className="pid-td pid-c-act">
                {s.manual ? (
                  <button type="button" className="pid-iconlink pid-iconlink-del" onClick={() => onRemove(s)} title="הסר השלמה">
                    <i className="fi fi-rr-trash" />
                  </button>
                ) : s.link ? (
                  <a className="pid-iconlink" href={s.link} target="_blank" rel="noreferrer" title="פתח מקור">
                    <i className="fi fi-rr-arrow-up-right-from-square" />
                  </a>
                ) : null}
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ── Box 3: gaps & completions ────────────────────────────────────────────────
// Uploading a missing document runs the spec §6.3 cycle: the file becomes a
// numbered SourceRecord and the gap is removed from this list.
function GapRow({ gap, onUpload, onRetryScrape, onManualLink }) {
  const ref = useRef(null);
  const [linkVal, setLinkVal] = useState('');
  // A source-ACCESS gap (graphic-log §2.4): the system couldn't reach the
  // database itself — offer a manual link + a retry, alongside the upload.
  const isAccessGap = gap.key === 'building_file_access';
  const saveLink = () => {
    const url = linkVal.trim();
    if (!url || !/^https?:\/\//i.test(url)) return;
    onManualLink?.(gap, url);
    setLinkVal('');
  };
  return (
    <div className={`pid-gap ${isAccessGap ? 'pid-gap-access' : ''}`}>
      <div className="pid-gap-text">
        <span className="pid-gap-name">{gap.name}</span>
        <span className="pid-gap-use">{gap.futureUse}</span>
        {isAccessGap && (
          <span className="pid-gap-linkrow">
            <input
              type="url"
              value={linkVal}
              onChange={(e) => setLinkVal(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') saveLink(); }}
              placeholder="הדביקו קישור לתיק הבניין (URL)…"
              aria-label="קישור ידני לתיק הבניין"
            />
            <button type="button" className="pid-gap-linksave" disabled={!/^https?:\/\//i.test(linkVal.trim())} onClick={saveLink}>
              שמירת קישור
            </button>
          </span>
        )}
      </div>
      {isAccessGap && onRetryScrape && (
        <button type="button" className="pid-gap-upload" onClick={onRetryScrape}>
          <i className="fi fi-rr-refresh" /> סריקה חוזרת
        </button>
      )}
      <button type="button" className="pid-gap-upload" onClick={() => ref.current?.click()}>
        <i className="fi fi-rr-cloud-upload" /> העלה
      </button>
      <input
        ref={ref}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png,.dwg,.dwf"
        style={{ display: 'none' }}
        onChange={(e) => { const f = e.target.files && e.target.files[0]; e.target.value = ''; if (f) onUpload(gap, f); }}
      />
    </div>
  );
}

function GapsBox({ missing, loading, onUpload, onRetryScrape, onManualLink }) {
  return (
    <section className="pid-card pid-gaps">
      <header className="pid-card-head">
        <i className="fi fi-rr-interrogation" />
        <h3>חוסרים והשלמות</h3>
      </header>
      <div className="pid-gaps-list">
        {loading && <div className="pid-table-loading"><span className="rp-spinner" /> מאתר חוסרים…</div>}
        {!loading && missing.length === 0 && <div className="pid-empty-note">כל החוסרים הושלמו.</div>}
        {!loading && missing.map((m) => (
          <GapRow key={m.id} gap={m} onUpload={onUpload} onRetryScrape={onRetryScrape} onManualLink={onManualLink} />
        ))}
      </div>
    </section>
  );
}

// ── Box 4: applicable plans ──────────────────────────────────────────────────
function PlansBox({ plans, loading }) {
  return (
    <section className="pid-card pid-plans">
      <header className="pid-card-head">
        <i className="fi fi-rr-document" />
        <h3>תכניות חלות על הנכס</h3>
        {!loading && <span className="pid-count">{plans.length}</span>}
      </header>
      <div className="pid-table">
        <div className="pid-tr pid-tr-head pid-plan-grid">
          <span className="pid-th pid-c-num">מס׳ תכנית</span>
          <span className="pid-th">שם התכנית</span>
          <span className="pid-th pid-c-type">סוג</span>
          <span className="pid-th pid-c-status">סטטוס</span>
          <span className="pid-th pid-c-date">תאריך אישור</span>
          <span className="pid-th pid-c-act" />
        </div>
        {loading && <div className="pid-table-loading"><span className="rp-spinner" /> מאתר תכניות חלות…</div>}
        {!loading && plans.length === 0 && <div className="pid-empty-note">לא נמצאו תכניות חלות על הנכס.</div>}
        {!loading && plans.map((p) => (
          <div className="pid-tr pid-plan-grid" key={p.id}>
            <span className="pid-td pid-c-num pid-plan-ref">{p.number}</span>
            <span className="pid-td"><span className="pid-src-name">{p.name}</span></span>
            <span className="pid-td pid-c-type">{p.type}</span>
            <span className="pid-td pid-c-status"><Pill tone={p.status.tone}>{p.status.label}</Pill></span>
            <span className="pid-td pid-c-date">{fmtDate(p.date)}</span>
            <span className="pid-td pid-c-act">
              {p.planUrl && (
                <a className="pid-iconlink" href={p.planUrl} target="_blank" rel="noreferrer" title="פתח תכנית">
                  <i className="fi fi-rr-arrow-up-right-from-square" />
                </a>
              )}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── Box 5: building-file categories (scraped from handasa) ───────────────────
function BuildingFileCategory({ cat }) {
  const [open, setOpen] = useState(false);
  const has = cat.count > 0;
  return (
    <li className={`pid-bfile-item ${has ? 'has-docs' : ''}`}>
      <button type="button" className="pid-bfile-row" onClick={() => has && setOpen((o) => !o)} disabled={!has}>
        <span className="pid-bfile-label">{cat.label}</span>
        <span className="pid-bfile-meta">
          {has ? (
            <>
              {cat.latest && <span className="pid-bfile-latest">עד {cat.latest}</span>}
              <span className="pid-bfile-count">{cat.count}</span>
              <i className={`fi ${open ? 'fi-rr-angle-small-up' : 'fi-rr-angle-small-down'}`} />
            </>
          ) : <span className="pid-bfile-pending">—</span>}
        </span>
      </button>
      {open && has && (
        <ul className="pid-bfile-docs">
          {cat.docs.map((d, i) => (
            <li key={i}>
              {d.link ? (
                <a className="pid-bfile-doc pid-bfile-doc-link" href={d.link} target="_blank" rel="noreferrer" title={`פתח · ${d.type}`}>
                  <span className="pid-bfile-doc-type">{d.type}</span>
                  <span className="pid-bfile-doc-date">{d.date || '—'}</span>
                  <i className="fi fi-rr-arrow-up-right-from-square" />
                </a>
              ) : (
                <div className="pid-bfile-doc">
                  <span className="pid-bfile-doc-type" title={d.type}>{d.type}</span>
                  <span className="pid-bfile-doc-date">{d.date || '—'}</span>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

function BuildingFileBox({ buildingFile, loading, gush, helka, onRetry }) {
  const bf = buildingFile || {};
  const cats = bf.categories || [];
  const scanning = loading || bf.loading;
  const hasParcel = Boolean(gush && helka);
  const handasaUrl = hasParcel
    ? `https://handasa.tel-aviv.gov.il/Pages/searchResultsAnonPageNew.aspx?block=${gush}_${helka}`
    : null;
  return (
    <section className="pid-card pid-bfile">
      <header className="pid-card-head">
        <i className="fi fi-rr-folder" />
        <h3>מסמכים מתיק הבניין</h3>
        {bf.scraped && <span className="pid-count">{bf.docCount}</span>}
        {handasaUrl && (
          <a className="pid-bfile-open" href={handasaUrl} target="_blank" rel="noreferrer">
            פתח תיק בניין <i className="fi fi-rr-arrow-up-right-from-square" />
          </a>
        )}
      </header>
      {scanning ? (
        <div className="pid-table-loading"><span className="rp-spinner" /> מושך מסמכים מתיק הבניין…</div>
      ) : !hasParcel ? (
        <div className="pid-empty-note">לא זוהו גוש/חלקה — לא ניתן למשוך את תיק הבניין.</div>
      ) : bf.error ? (
        <div className="pid-bfile-fail">
          <div className="pid-bfile-fail-text">
            <i className="fi fi-rr-exclamation" />
            <span>משיכת המסמכים מתיק הבניין נכשלה (כשל טכני / מקור המידע לא הגיב).</span>
          </div>
          <div className="pid-bfile-fail-actions">
            {onRetry && (
              <button type="button" className="pid-bfile-retry" onClick={onRetry}>
                <i className="fi fi-rr-refresh" /> נסה שוב
              </button>
            )}
            {handasaUrl && (
              <a className="pid-bfile-retry pid-bfile-retry-link" href={handasaUrl} target="_blank" rel="noreferrer">
                פתח ידנית <i className="fi fi-rr-arrow-up-right-from-square" />
              </a>
            )}
          </div>
        </div>
      ) : bf.scraped && bf.docCount === 0 ? (
        <div className="pid-empty-note">לא נמצאו מסמכים בתיק הבניין עבור נכס זה.</div>
      ) : (
        <ul className="pid-bfile-list">
          {cats.map((c) => <BuildingFileCategory key={c.key} cat={c} />)}
        </ul>
      )}
    </section>
  );
}

// In-memory view-model cache (per project), so navigating back to this screen
// restores the computed result + agent log INSTANTLY — no blank/reload flash.
// The DB snapshot is the durable store; this just avoids the re-fetch blink.
const VM_CACHE = new Map();

// The signature is "<version>|<property fields>". Reuse decisions compare only the
// property part (drop the version), so changing derivation logic never re-runs
// completed work (scrape, autofill, log) for an unchanged property.
const propKey = (sig) => String(sig || '').split('|').slice(1).join('|');

export default function PropertyIdentification({ project, onBack, onNext, onPrev, onGoStage }) {
  const name = project?.name || 'הפרויקט';
  // Initialize straight from the in-memory cache so a remount paints the cached
  // result + log on the FIRST render — no blank/loading flash on navigation.
  const [vm, setVm] = useState(() => VM_CACHE.get(project?.id) || null);
  const [loading, setLoading] = useState(() => !VM_CACHE.has(project?.id));
  const [error, setError] = useState('');
  const [loadedProject, setLoadedProject] = useState(null); // full project, for "הקודם"
  const [savingDraft, setSavingDraft] = useState(false);

  // Keep the in-memory cache in sync with the latest vm (building-file + log
  // changes included) so a remount restores the exact current state.
  useEffect(() => { if (vm && project?.id) VM_CACHE.set(project.id, vm); }, [vm, project?.id]);

  useEffect(() => {
    let alive = true;
    if (!project?.id) { setLoading(false); setError('לא נבחר פרויקט.'); return undefined; }
    setError('');
    // Instant restore from memory — the agent log is a history; it must not blink
    // away and reload when navigating back to this step.
    const cached = VM_CACHE.get(project.id);
    if (cached) { setVm(cached); setLoading(false); } else { setLoading(true); }
    (async () => {
      try {
        const loaded = await getProject(project.id);
        if (alive) setLoadedProject(loaded);
        // Reuse is keyed by the PROPERTY only (ignore the code/version prefix), so
        // a logic change never forces re-running completed steps — the scrape and
        // the agent log are preserved as long as the property hasn't changed.
        const want = propKey(propertySignature(loaded.property));

        // Cached vm already matches the property — nothing to redo.
        if (cached && propKey(cached.signature) === want) {
          if (alive && !cached.buildingFile?.docCount && cached.identification?.gush && cached.identification?.helka) {
            runBuildingFileScrape(cached.identification.gush, cached.identification.helka);
          }
          return;
        }
        // DB snapshot matches the property — use it as-is (no re-derive/re-scrape).
        const saved = loaded.project?.identification;
        if (saved && saved.signature && propKey(saved.signature) === want) {
          if (alive) { setVm(saved); setLoading(false); }
          if (alive && !saved.buildingFile?.docCount && saved.identification?.gush && saved.identification?.helka) {
            runBuildingFileScrape(saved.identification.gush, saved.identification.helka);
          }
          return;
        }

        // Property changed or never identified — re-derive (show loading only if
        // we had nothing cached to display).
        if (alive && !cached) setLoading(true);
        const result = await identifyProperty(loaded);
        if (!alive) return; // a newer effect run owns the screen (StrictMode/nav)
        setVm(result);
        saveIdentification(project.id, result).catch(() => {}); // persist the §8 snapshot
        if (result.identification?.gush && result.identification?.helka) {
          runBuildingFileScrape(result.identification.gush, result.identification.helka);
        }
      } catch (e) {
        if (alive) setError('טעינת זיהוי הנכס נכשלה. נסו שוב.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id]);

  // §6.3 — a missing document is uploaded: it becomes a numbered source (labelled
  // by the gap it completes) and the gap disappears. Re-persist the snapshot.
  const handleGapUpload = async (gap, file) => {
    // Store the file in Vercel Blob so it's openable from the archive; fall back
    // to metadata-only if the upload fails.
    let link = null;
    try { link = await uploadFile(file); } catch { /* metadata only */ }
    setVm((prev) => {
      if (!prev) return prev;
      const number = prev.sources.reduce((mx, s) => Math.max(mx, s.number || 0), 0) + 1;
      const next = {
        ...prev,
        sources: [...prev.sources, { ...manualSource(file, number, gap), link }],
        missing: prev.missing.filter((m) => m.id !== gap.id),
      };
      if (project?.id) saveIdentification(project.id, next).catch(() => {});
      return next;
    });
  };

  // Building file (תיק בניין) — slow handasa scrape (~15-30s). Runs async after
  // the main load, updates just the building-file box, and caches the result in
  // the snapshot so it's scraped only once.
  // Only touches buildingFile — the matching "מקורות שנאספו" row is DERIVED from
  // this at render (see displaySources), so there's no second mutation to race.
  // Append/replace the building-file entry in the agent log (it's a single
  // action that transitions running → done/error). Keeps other events intact.
  const withBfEvent = (log, event) => [...(log || []).filter((e) => !e.id.startsWith('evt-bf')), event];

  const runBuildingFileScrape = (gush, helka) => {
    setVm((prev) => (prev ? {
      ...prev,
      log: withBfEvent(prev.log, { id: 'evt-bf-running', time: nowTime(), text: 'סורק תיק בניין…', kind: 'running' }),
      buildingFile: { ...(prev.buildingFile || {}), loading: true, error: false },
    } : prev));
    fetchBuildingFile(gush, helka)
      .then((data) => {
        setVm((prev) => {
          if (!prev) return prev;
          const event = buildingFileLogEvent(data.docCount ? `תיק בניין — ${data.docCount} מסמכים` : 'תיק בניין — לא נמצאו מסמכים', data.docCount ? 'done' : 'info');
          const next = { ...prev, log: withBfEvent(prev.log, event), buildingFile: { scraped: true, loading: false, categories: data.categories || [], docCount: data.docCount || 0, fetchedAt: data.fetchedAt } };
          if (project?.id) saveIdentification(project.id, next).catch(() => {});
          return next;
        });
      })
      .catch(() => {
        setVm((prev) => (prev ? {
          ...prev,
          log: withBfEvent(prev.log, buildingFileLogEvent('סריקת תיק בניין נכשלה', 'error')),
          buildingFile: { ...(prev.buildingFile || {}), loading: false, error: true },
        } : prev));
      });
  };

  // Remove a manually-uploaded source — and restore the gap it completed so the
  // user can re-upload (redo).
  const handleRemoveSource = (src) => {
    setVm((prev) => {
      if (!prev) return prev;
      const sources = prev.sources.filter((s) => s.id !== src.id);
      const missing = src.gap && !prev.missing.some((m) => m.id === src.gap.id)
        ? [...prev.missing, src.gap]
        : prev.missing;
      const next = { ...prev, sources, missing };
      if (project?.id) saveIdentification(project.id, next).catch(() => {});
      return next;
    });
  };

  // "שמירת טיוטה" — mark the project a draft and return to the gallery (the
  // identification snapshot is already persisted). Mirrors screen 1.
  const handleSaveDraft = async () => {
    if (!project?.id) { onBack?.(); return; }
    setSavingDraft(true);
    try { await updateProject(project.id, { status: 'draft' }); onBack?.(); }
    catch { setError('שמירת הטיוטה נכשלה. נסו שוב.'); setSavingDraft(false); }
  };

  const id = vm?.identification;
  const parcel = vm?.parcel;

  // The "תיק בניין" source row mirrors the live building-file scrape state, so the
  // table and the building-file box never disagree (and no save race).
  const bf = vm?.buildingFile || {};
  const displaySources = (vm?.sources || []).map((s) => {
    // Re-derive the daf-meyda link from the current gush/חלקה so it works even
    // for snapshots saved before this source existed.
    if (s.kind === 'daf_meyda' || /דף זכויות/.test(s.source || '')) {
      const link = (id?.gush && id?.helka)
        ? `https://gisn.tel-aviv.gov.il/medamukdam/fr_asp/fr_meda_single.asp?gush=${id.gush}&helka=${id.helka}&opt=2&status=0&street=0&house=0`
        : s.link;
      return { ...s, source: 'דף זכויות (מידע מוקדם — עיריית ת״א)', link, status: link ? 'collected' : 'empty', note: link ? 'דף זכויות רשמי לנכס' : 'לא נמצא' };
    }
    // The GIS constraint layers are pulled when the statutory stage opens; once
    // that snapshot carries the land use, flip this row from "ייאסף בניתוח" to
    // "נאסף" so revisiting stage 2 reflects what was actually collected.
    if (s.databaseId === 'db_gis_tel_aviv') {
      const lu = loadedProject?.project?.statutory?.landUse;
      const collected = Boolean(lu && lu.conservation && lu.trees && lu.transit);
      if (!collected) return s;
      return {
        ...s, status: 'collected', note: 'נאסף בשלב הניתוח הסטטוטורי',
        datasets: (s.datasets || []).map((d) => ({ ...d, status: 'collected' })),
      };
    }
    // An expected-manual database (טאבו / מדידה / תיעוד חזותי) turns collected
    // once the user uploads its completion (the manual source carries the gap key).
    if (s.kind === 'manual_expected') {
      const uploaded = (vm?.sources || []).some((m) => m.manual && m.gap?.key === s.gapKey);
      return uploaded ? { ...s, status: 'collected', note: 'הועלה ידנית' } : s;
    }
    if (s.kind !== 'building_file') return s;
    if (bf.loading) return { ...s, status: 'scanning', note: 'סורק…' };
    if (bf.error) {
      return bf.manualLink
        ? { ...s, status: 'failed', link: bf.manualLink, note: 'הסריקה נכשלה — קושר קישור ידני' }
        : { ...s, status: 'failed', note: 'הסריקה נכשלה' };
    }
    if (bf.scraped) return { ...s, status: bf.docCount ? 'collected' : 'empty', note: bf.docCount ? `${bf.docCount} מסמכים` : 'לא נמצאו מסמכים' };
    return s;
  });

  // §2.4 — a manually-pasted link for an unreachable source: stored on the
  // building-file state so the source row carries it (openable by the user and
  // the agent) and survives the snapshot save. The automated re-pull FROM the
  // pasted URL is a backend follow-up (host-whitelisting required).
  const handleManualSourceLink = (gap, url) => {
    if (gap.key !== 'building_file_access') return;
    setVm((prev) => {
      if (!prev) return prev;
      const next = { ...prev, buildingFile: { ...(prev.buildingFile || {}), manualLink: url } };
      if (project?.id) saveIdentification(project.id, next).catch(() => {});
      return next;
    });
  };

  // Gaps are SOURCE-level (graphic-log §2.3): when the tik-binyan scrape fails,
  // the gap is "can't access the building file" — its documents (טופס 4 etc.)
  // are never free-standing gaps. Old snapshots may still carry the retired
  // tofes4 document-gap; drop it and normalize names to the registry's (§2.5).
  const GAP_SOURCE_NAMES = { tabu: 'טאבו / נסח מקרקעין', survey: 'מדידה / תכנית מצב קיים', photos: 'תיעוד חזותי / Street View' };
  const displayMissing = [
    ...(bf.error ? [{
      id: 'miss-building-file-access',
      key: 'building_file_access',
      name: 'לא ניתן לגשת לתיק הבניין',
      futureUse: 'העלו מסמכים מתיק הבניין ידנית, או הריצו סריקה חוזרת מתיבת תיק הבניין.',
      importance: 'high',
    }] : []),
    ...(vm?.missing || [])
      .filter((m) => m.key !== 'tofes4')
      .map((m) => (GAP_SOURCE_NAMES[m.key] ? { ...m, name: GAP_SOURCE_NAMES[m.key] } : m)),
  ];

  return (
    <div className="pid">
      <nav className="np-crumbs">
        <button type="button" onClick={onBack}>פרויקטים</button>
        <i className="fi fi-rr-angle-small-left" />
        <span className="pid-crumb-name">{name}</span>
        <i className="fi fi-rr-angle-small-left" />
        <span>זיהוי ואיסוף נתונים</span>
      </nav>

      {/* Full-width header — keeps the title at the page's start and lets the rail
          (agent activity) align with the content boxes below it (spec 5.2/5.3). */}
      <header className="pid-workhead">
        <div className="pid-workhead-text">
          <h1>{name}</h1>
          <p>זיהוי הנכס ואיסוף המקורות — בסיס למצב הסטטוטורי שייבנה בשלב הבא.</p>
        </div>
        <div className="pid-workhead-actions">
          <button
            type="button"
            className="np-btn np-btn-secondary pid-prev"
            onClick={() => onPrev?.(loadedProject)}
            disabled={loading || !loadedProject}
          >
            <i className="fi fi-rr-angle-small-right" /> הקודם
          </button>
          <button
            type="button"
            className="np-btn np-btn-secondary"
            onClick={handleSaveDraft}
            disabled={loading || savingDraft}
          >
            <i className="fi fi-rr-bookmark" /> {savingDraft ? 'שומר…' : 'שמירת טיוטה'}
          </button>
          <button
            type="button"
            className="np-btn np-btn-primary pid-next"
            onClick={() => onNext?.(project)}
            disabled={loading || !id || id.status === 'unidentified'}
          >
            הבא <i className="fi fi-rr-angle-small-left" />
          </button>
        </div>
      </header>

      {error && <div className="pid-error" role="alert"><i className="fi fi-rr-exclamation" /> {error}</div>}

      <div className="pid-layout">
        {/* Right rail (narrow) — agent activity, roadmap, document archive. The
            archive sits inline in the (sticky) rail so it never overlaps nav. */}
        <aside className="pid-rail">
          <AgentActivity log={vm?.log || []} loading={loading} />
          {/* Forward nav to stage 3 is offered once the project has a persisted
              statutory snapshot (i.e. the user already reached it). */}
          <Roadmap
            activeStage={ACTIVE_STAGE}
            reachedStage={Math.max(ACTIVE_STAGE, reachedStageFor(loadedProject?.project))}
            onStageClick={(i) => onGoStage?.(i, { loaded: loadedProject, project })}
          />
          <DocumentArchive inline projectId={project?.id} />
        </aside>

        {/* Work area: the wide tables (sources, applicable plans) span the full
            width so their rows aren't cramped; the narrower gaps + building-file
            boxes share an equal two-column row below. */}
        <div className="pid-work">
          <IdentificationBox id={id} parcel={parcel} loading={loading} />
          <SourcesBox sources={displaySources} loading={loading} onRemove={handleRemoveSource} />
          {/* Gaps + building-file sit above the applicable-plans table, which can
              run long. */}
          <div className="pid-cols">
            <div className="pid-col">
              <GapsBox
                missing={displayMissing}
                loading={loading}
                onUpload={handleGapUpload}
                onRetryScrape={id?.gush && id?.helka ? () => runBuildingFileScrape(id.gush, id.helka) : null}
                onManualLink={handleManualSourceLink}
              />
            </div>
            <div className="pid-col">
              <BuildingFileBox
                buildingFile={vm?.buildingFile}
                loading={loading}
                gush={id?.gush}
                helka={id?.helka}
                onRetry={id?.gush && id?.helka ? () => runBuildingFileScrape(id.gush, id.helka) : null}
              />
            </div>
          </div>
          <PlansBox plans={vm?.plans || []} loading={loading} />
        </div>
      </div>
    </div>
  );
}
